/* eslint react-hooks/immutability: ["warn"] */
import React, { useRef } from "react";
import { useUploadImageToCloud } from "../hooks/useUploadImageToCloud";
import { ImageUploadResponse } from "../types/images";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import PlusIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/PlusIconPath";
import * as Sentry from "@sentry/react";
import imageCompression from "browser-image-compression";
import { useTranslation } from "@coworker/locales";

interface UploadImageButtonProps {
  onImageUploadStart: (fileList: FileList) => void | undefined;
  onImageUploadEnd: (fileList: ImageUploadResponse[]) => void;
  isUploadingImages: boolean;
}

export const UploadImageButton = ({
  onImageUploadStart,
  onImageUploadEnd,
  isUploadingImages,
}: UploadImageButtonProps) => {
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const uploadImageToCloud = useUploadImageToCloud();
  const fileList: ImageUploadResponse[] = [];
  const { t } = useTranslation();

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return;
    }

    onImageUploadStart(event.target.files);

    for (const fileUploaded of event.target.files) {
      if (!fileUploaded) continue;

      // Resize the image to 1000x1000
      const options = {
        maxWidthOrHeight: 1000,
      };
      const resizedImage = await imageCompression(fileUploaded, options);

      const uploadedImage = await Sentry.startSpan(
        { name: "media.uploadImageToCloud" },
        async () => {
          return await uploadImageToCloud.mutateAsync(resizedImage);
        }
      );
      if (uploadedImage) fileList.push(uploadedImage);
    }

    onImageUploadEnd(fileList);
  };

  const handleButtonClick = () => {
    if (hiddenFileInput.current) {
      hiddenFileInput.current.click();
    }
  };

  return (
    <>
      <FixaButton
        type={"secondary"}
        onClick={handleButtonClick}
        text={t("uploadImagesString")}
        loading={uploadImageToCloud.status === "pending" || isUploadingImages}
        ssrIcon={PlusIconPath}
        iconPosition="trailing"
        size="small"
      />
      <input
        type="file"
        ref={hiddenFileInput}
        onChange={handleChange}
        style={{ display: "none" }}
        multiple
      />
    </>
  );
};
